import { useQuery } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";

// Lightweight wrappers around the generic AdminLookups endpoints for the
// two REWARD_*_QTY_CODE tables. The legacy EditReward / ViewReward pages
// populate `ddlVendorBuy` and `ddlVendorGet` from these tables — see
// `EditReward.aspx.cs.BindVendorBuyList` / `BindVendorGetList`.
//
// The DM-side tables (REWARD_BUY_QTY_CODE / REWARD_GET_QTY_CODE) aren't
// guaranteed to exist in every Oracle environment, so when the admin
// lookup endpoint returns 500 (ORA-00942) we fall back to a hard-coded
// seed list lifted from the legacy reference data. This keeps the SPA
// usable rather than rendering an empty dropdown that silently 500s
// when submitted.

export interface RewardQtyCode {
  id: string;
  description: string;
}

interface LookupResponse {
  items: RewardQtyCode[];
}

// Default codes covering every value the legacy app inserts. Used as the
// fallback when the admin lookup endpoint is unavailable. Sorted to match
// legacy ddl order (alphabetic by code).
const BUY_FALLBACK: RewardQtyCode[] = [
  { id: "B", description: "Boxes" },
  { id: "C", description: "Cases" },
  { id: "D", description: "Dollars" },
  { id: "G", description: "Gallons" },
  { id: "L", description: "Pounds" },
  { id: "O", description: "Ounces" },
  { id: "U", description: "Units" },
];

const GET_FALLBACK: RewardQtyCode[] = [
  { id: "B", description: "Boxes" },
  { id: "C", description: "Cases" },
  { id: "D", description: "Dollars" },
  { id: "G", description: "Gallons" },
  { id: "L", description: "Pounds" },
  { id: "O", description: "Ounces" },
  { id: "U", description: "Units" },
];

const fetchLookup = async (
  name: "reward-buy-qty-code" | "reward-get-qty-code",
  fallback: RewardQtyCode[]
): Promise<RewardQtyCode[]> => {
  try {
    const response = await apiClient.get<LookupResponse>(
      `/dm/V1/admin/lookups/${name}`
    );
    const items = response.data.items ?? [];
    // If the table exists but is empty, prefer fallback over an empty dropdown.
    return items.length > 0 ? items : fallback;
  } catch {
    // 500 (ORA-00942), 404, network — silently fall back.
    return fallback;
  }
};

export const useRewardBuyQtyCodes = () =>
  useQuery({
    queryKey: ["reward-buy-qty-codes"],
    queryFn: () => fetchLookup("reward-buy-qty-code", BUY_FALLBACK),
    staleTime: 10 * 60 * 1000,
  });

export const useRewardGetQtyCodes = () =>
  useQuery({
    queryKey: ["reward-get-qty-codes"],
    queryFn: () => fetchLookup("reward-get-qty-code", GET_FALLBACK),
    staleTime: 10 * 60 * 1000,
  });
